/**
 * bindingGenerators.js
 *
 * Strategy-driven Jinja2 expression generators for AutomationBinding —
 * see docs/Binding_Architecture_FINAL.md §5.
 *
 * Exactly one generator function per strategy. Dispatch is purely
 * `binding.strategy` → function; no generator here inspects a widget
 * property name or widget type. All widget-specific defaults (the
 * historical color-toggle / LED-brightness behavior) are resolved
 * before a binding ever reaches this module — either already frozen
 * into a 'template' binding by migration (see
 * migration/LegacyEventsToAst.js), or expressed directly as `rules`
 * data on a 'mapping' binding.
 *
 * This module supersedes propertyExpression.js's buildBindingExpression()
 * for AutomationBinding-backed properties. propertyExpression.js itself
 * remains — see its own header comment — as the separate, transitional
 * generator for the legacy (pre-AutomationBinding) hass_config.properties
 * row shape.
 *
 * Evaluation order (see "Feature 6 — Generate Home Assistant Jinja for
 * Value Transforms" and the Formatter layer):
 *
 *     Entity
 *       ↓
 *     Transforms   (binding.transforms — see valueTransforms.js for the
 *       ↓           editor-side/preview equivalent this must match)
 *     Formatter    (binding.formatter — see valueFormatters.js;
 *       ↓           state/aggregate only, see generateState())
 *     Strategy
 *       ↓
 *     Generated Jinja
 *
 * `generateTransformPipeline()` is the single helper both entity-reading
 * strategies (generateState, generateMapping) use to apply
 * `binding.transforms` to the raw entity value before their own logic
 * runs — neither duplicates transform knowledge. 'constant' and
 * 'template' have no entity-derived incoming value to transform (a
 * constant is a fixed literal; a template is the designer's own raw
 * Jinja) and are deliberately left untouched — no editor path ever sets
 * transforms for those strategies today.
 *
 * Formatter rendering (renderFormatIntent() and its FORMAT_INTENT_RENDERERS
 * registry, below) is intentionally the only formatter-related code in
 * this file — it does no more than mechanically translate an
 * already-resolved, domain-agnostic FormatIntent into Jinja syntax.
 * Deciding *which* formatting is needed (business logic — reading
 * binding.formatter, choosing which value variant applies) lives
 * entirely in valueFormatters.js's buildFormatIntent(), mirroring how
 * that file's applyTransform() (business logic) is paired with this
 * file's jinjaTransformStep() (Jinja rendering) for transforms.
 */
import { buildFormatIntent } from '../schema/valueFormatters.js';
import { colorAttributeToJinja } from '../schema/colorNormalizer.js';

/** @private */
function generateConstant(binding) {
  return binding.value;
}

/** @private */
function generateTemplate(binding) {
  return binding.template;
}

// ── Transform pipeline (Jinja2) ─────────────────────────────────────────────
//
// Mirrors valueTransforms.js's runTransformPipeline() semantics exactly,
// in Jinja2 instead of JS: every arithmetic step coerces its input via
// Jinja's `float` filter first (which — like JS's `Number()` — defaults
// to 0 on a non-numeric input), so chained numeric transforms behave
// identically whether they run in the browser preview or in the
// generated template. All filters used (`int`, `float`, `round`, `abs`,
// `min`, `max`, `upper`, `lower`, `trim`) are standard Jinja2 builtins —
// no Home-Assistant-specific extension is assumed.

/** @private */
function escapeJinjaString(value) {
  return String(value).replace(/"/g, '\\"');
}

/** @private */
function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * One transform step's Jinja rendering — wraps `expr` (itself a Jinja
 * expression, not a value) and returns the new, larger expression.
 * @private
 */
function jinjaTransformStep(step, expr) {
  const p = step.params ?? {};
  switch (step.type) {
    case 'integer':   return `(${expr}) | int`;
    case 'float':     return `(${expr}) | float`;
    case 'round':     return `(${expr}) | float | round(${toNumber(p.decimals)})`;
    case 'abs':       return `(${expr}) | float | abs`;
    case 'clamp':     return `[[(${expr}) | float, ${toNumber(p.max)}] | min, ${toNumber(p.min)}] | max`;
    case 'multiply':  return `(${expr}) | float * ${toNumber(p.factor)}`;
    case 'divide': {
      const divisor = toNumber(p.divisor);
      // A zero divisor is a fixed, design-time literal here, never a
      // runtime value — so this branch is either always or never taken
      // for a given binding. JS's runtime preview divides by a literal
      // zero and gets Infinity/-Infinity/NaN (see valueTransforms.js);
      // Jinja/Python's `/` raises ZeroDivisionError even for floats, so
      // that exact value can't be reproduced without crashing template
      // rendering for the whole widget. validateTransformPipeline.js
      // already warns the designer about this before it gets here;
      // generation falls back to a literal 0 rather than emit a
      // template that would fail at Home Assistant's runtime — a
      // documented, deliberate departure from bit-for-bit preview parity
      // for this one already-invalid input only.
      if (divisor === 0) return '0';
      return `(${expr}) | float / ${divisor}`;
    }
    case 'add':       return `(${expr}) | float + ${toNumber(p.amount)}`;
    case 'subtract':  return `(${expr}) | float - ${toNumber(p.amount)}`;
    case 'prefix':    return `"${escapeJinjaString(p.text ?? '')}" ~ (${expr})`;
    case 'suffix':    return `(${expr}) ~ "${escapeJinjaString(p.text ?? '')}"`;
    case 'uppercase': return `(${expr}) | upper`;
    case 'lowercase': return `(${expr}) | lower`;
    case 'trim':      return `(${expr}) | trim`;
    default:          return expr;
  }
}

/**
 * Apply `binding.transforms` (an ordered pipeline, see valueTransforms.js)
 * to a Jinja value expression. Returns `valueExpr` unchanged when the
 * pipeline is empty — the single point that guarantees bindings without
 * transforms generate byte-identical output to before this feature.
 *
 * @param {Array<{type: string, params: Object}>|undefined} transforms
 * @param {string} valueExpr — a Jinja expression, e.g. `states("sensor.x")`
 * @returns {string}
 */
function generateTransformPipeline(transforms, valueExpr) {
  return (transforms ?? []).reduce((expr, step) => jinjaTransformStep(step, expr), valueExpr);
}

/**
 * The Jinja expression for a binding's *incoming* value — the entity read
 * (state or attribute) with `binding.transforms` applied. Exactly the
 * expression generateState()/generateMapping() build for themselves
 * below, exported so editors can offer it to the designer without
 * re-deriving it: a mapping rule's `result` is emitted verbatim into the
 * generated template, so pasting this expression into a result is how a
 * composed string refers to the value the mapping just matched — plain
 * Jinja, no new variable syntax (see StringBuilderField.vue /
 * MappingBindingEditor.vue's `builderVariables`).
 *
 * @param {import('../ast/AutomationBinding.js').AutomationBinding} binding
 * @returns {string} e.g. `states("sensor.x")`, or `(states("sensor.x")) | float | abs`
 */
export function buildIncomingValueExpression(binding) {
  const entityId = String(binding.entityId ?? '').trim();
  if (!entityId) return '';
  const entityRef = `"${escapeJinjaString(entityId)}"`;
  const rawValueExpr = binding.strategy === 'state'
    ? (binding.valueSource === 'attribute' ? `state_attr(${entityRef}, "${binding.attribute}")` : `states(${entityRef})`)
    : binding.attribute
    ? `state_attr(${entityRef}, "${binding.attribute}")`
    : `states(${entityRef})`;
  return generateTransformPipeline(binding.transforms, rawValueExpr);
}

// ── Formatter rendering (Jinja2) ────────────────────────────────────────────
//
// Each renderer's only job: turn an already-resolved FormatIntent (see
// valueFormatters.js's buildFormatIntent()) into Jinja syntax. No
// formatting business logic here. Registry-dispatched, same pattern as
// BINDING_STRATEGIES/BINDING_GENERATORS/TRANSFORM_TYPES elsewhere in this
// codebase, rather than one growing switch statement.

/** @private */
function renderColorIntent(intent) {
  return colorAttributeToJinja(intent.value, intent.attribute);
}

/** @private */
function renderPercentageIntent(intent) {
  return `{{ ${percentageValueExpression(intent.value)} }}`;
}

/** @private */
function percentageValueExpression(valueExpr) {
  return `((${valueExpr}) | float * 100 / 255) | round(0)`;
}

/** @private */
function renderDatetimeIntent(intent) {
  switch (intent.style) {
    case 'relative': return `{{ relative_time(as_datetime(${intent.value})) }}`;
    case 'date':     return `{{ as_datetime(${intent.value}).strftime("%Y-%m-%d") }}`;
    case 'time':     return `{{ as_datetime(${intent.value}).strftime("%H:%M") }}`;
    case 'datetime': return `{{ as_datetime(${intent.value}).strftime("%Y-%m-%d %H:%M") }}`;
    case 'duration':
      return (
        `{% set diff = (as_timestamp(now()) - as_timestamp(${intent.value})) | int %}` +
        `{{ (diff // 3600) ~ "h " ~ ((diff % 3600) // 60) ~ "m" }}`
      );
    default:
      return `{{ ${intent.value} }}`;
  }
}

const FORMAT_INTENT_RENDERERS = {
  color:      renderColorIntent,
  percentage: renderPercentageIntent,
  datetime:   renderDatetimeIntent,
};

/** @private — dispatches an already-resolved intent to its renderer. No formatting business logic, only syntax. */
function renderFormatIntent(intent) {
  return FORMAT_INTENT_RENDERERS[intent.kind](intent);
}

/**
 * String presentation transforms are useful after a numeric formatter (for
 * example Percentage + Suffix "%"). Keep the existing transform pipeline
 * semantics for every other formatter, while allowing this natural UI flow.
 * Numeric transforms still run before the Percentage conversion.
 * @private
 */
function renderPercentageBinding(binding, rawValueExpr) {
  const presentationTypes = new Set(['prefix', 'suffix', 'uppercase', 'lowercase', 'trim']);
  const numericTransforms = (binding.transforms ?? []).filter(step => !presentationTypes.has(step.type));
  const presentationTransforms = (binding.transforms ?? []).filter(step => presentationTypes.has(step.type));
  const numericValueExpr = generateTransformPipeline(numericTransforms, rawValueExpr);
  const valueExpr = presentationTransforms.reduce(
    (expr, step) => jinjaTransformStep(step, expr),
    percentageValueExpression(numericValueExpr),
  );
  return `{{ ${valueExpr} }}`;
}

/**
 * Entity-state read. Used directly by the 'state' strategy, and reused
 * by 'mapping' (below) for the two cases where a mapping degrades to a
 * plain state/attribute read — matching the pre-existing behavior of
 * the source this replaces, where an attribute target always won over
 * on/off values, and empty on/off values fell through to a plain state
 * read.
 * @private
 */
function generateState(binding) {
  const entityRef = `"${binding.entityId}"`;
  const filter    = binding.filter ? ` ${binding.filter}` : '';
  const fallback  = binding.fallback ?? '';
  const attr      = binding.valueSource === 'attribute' ? binding.attribute : '';

  const rawValueExpr         = binding.valueSource === 'attribute'
    ? `state_attr(${entityRef}, "${attr}")`
    : `states(${entityRef})`;
  const transformedValueExpr = generateTransformPipeline(binding.transforms, rawValueExpr);

  // Formatter — takes over the binding's entire output shape when active
  // (bypassing filter/fallback below), exactly matching the pre-existing
  // rgb_color special case's behavior, generalized across formatter types.
  const intent = buildFormatIntent(binding, { rawValueExpr, valueExpr: transformedValueExpr });
  if (intent?.kind === 'percentage' && (binding.transforms ?? []).some(step =>
    ['prefix', 'suffix', 'uppercase', 'lowercase', 'trim'].includes(step.type))) {
    return renderPercentageBinding(binding, rawValueExpr);
  }
  if (intent) return renderFormatIntent(intent);

  const valueExpr = transformedValueExpr + filter;

  // Specific state attribute (brightness, current_position, …)
  if (attr) {
    if (fallback !== '') {
      return `{{ ${valueExpr} if state_attr(${entityRef}, "${attr}") is not none else "${fallback}" }}`;
    }
    return `{{ ${valueExpr} }}`;
  }

  // Entity state with optional filter; guarded by has_value() when fallback is set
  if (fallback !== '') {
    return `{{ ${valueExpr} if has_value(${entityRef}) else "${fallback}" }}`;
  }
  return `{{ ${valueExpr} }}`;
}

/**
 * One mapping rule's comparison, evaluated against the entity's own
 * state or `attribute` — or, when a transform pipeline is present, against
 * the transformed value instead (transformedExpr is non-null exactly when
 * `binding.transforms` is non-empty). The no-transforms state branch is
 * byte-identical to this function's pre-Feature-6 form.
 * @private
 */
function compareExpression(entityRef, operator, value, transformedExpr, attribute = '') {
  if (transformedExpr === null && attribute) {
    const read = `state_attr(${entityRef}, "${attribute}")`;
    // An attribute keeps its type and is none while missing (brightness of
    // a light that is off): compare numbers numerically, missing as 0.
    if (/^-?\d+(\.\d+)?$/.test(String(value)) || !['==', '!='].includes(operator)) {
      return `${read} | float(0) ${operator} ${value}`;
    }
    return `${read} ${operator} "${value}"`;
  }
  if (transformedExpr === null) {
    if (operator === '==') return `is_state(${entityRef}, "${value}")`;
    if (operator === '!=') return `not is_state(${entityRef}, "${value}")`;
    // Ordering operators compare numerically — states() is always a string.
    return `states(${entityRef}) | float ${operator} ${value}`;
  }

  if (operator === '==') return `(${transformedExpr}) == "${value}"`;
  if (operator === '!=') return `(${transformedExpr}) != "${value}"`;
  return `${transformedExpr} ${operator} ${value}`;
}

/**
 * Mapping strategy — evaluates `binding.rules` in declared order (first
 * match wins; a `when: null` entry is the else/default) and emits the
 * matching rule's `result`. See docs/Binding_Architecture_FINAL.md §3
 * for the full rule-evaluation model.
 * @private
 */
function generateMapping(binding) {
  // No rules configured yet degrades to a plain state/attribute read, as
  // does an attribute with an active formatter — the legacy rgb_color
  // path (see LegacyEventsToAst.js _normalizeBindingFormatter).
  // generateState() applies binding.transforms itself in that case.
  const formatted = Boolean(binding.formatter?.type) && binding.formatter.type !== 'none';
  if ((binding.rules ?? []).length === 0 || (binding.attribute && formatted)) {
    return generateState(binding);
  }

  const entityRef = `"${binding.entityId}"`;
  const fallback  = binding.fallback ?? '';
  const attribute = binding.attribute ?? '';

  const hasTransforms = (binding.transforms ?? []).length > 0;
  const transformedExpr = hasTransforms
    ? generateTransformPipeline(binding.transforms,
      attribute ? `state_attr(${entityRef}, "${attribute}")` : `states(${entityRef})`)
    : null;

  const branches = [];
  let opened = false;

  if (fallback !== '') {
    branches.push(`{% if not has_value(${entityRef}) %}${fallback}`);
    opened = true;
  }

  for (const rule of binding.rules) {
    if (!rule.when) {
      branches.push(opened ? `{% else %}${rule.result}` : `{% if true %}${rule.result}`);
      opened = true;
      continue;
    }
    const cond = compareExpression(entityRef, rule.when.operator, rule.when.value, transformedExpr, attribute);
    branches.push(`{% ${opened ? 'elif' : 'if'} ${cond} %}${rule.result}`);
    opened = true;
  }

  return `${branches.join('')}{% endif %}`;
}

/**
 * Switch strategy — compares the entity's state or attribute with
 * `binding.cases` in order (the same comparisons as mapping rules) and
 * emits the matching case's own binding expression; a `when: null` case is
 * the else. A case binding without a value (e.g. no entity) emits nothing.
 * @private
 */
function generateSwitch(binding) {
  const entityRef = `"${binding.entityId}"`;
  const branches = [];
  for (const item of binding.cases ?? []) {
    const expr = buildBindingExpression(item.binding) ?? '';
    const opened = branches.length > 0;
    if (!item.when) {
      branches.push(opened ? `{% else %}${expr}` : `{% if true %}${expr}`);
      continue;
    }
    const cond = compareExpression(entityRef, item.when.operator, item.when.value, null, binding.attribute ?? '');
    branches.push(`{% ${opened ? 'elif' : 'if'} ${cond} %}${expr}`);
  }
  return branches.length > 0 ? `${branches.join('')}{% endif %}` : null;
}

export const BINDING_GENERATORS = {
  constant: generateConstant,
  state:    generateState,
  mapping:  generateMapping,
  template: generateTemplate,
  switch:   generateSwitch,
};

/**
 * Single dispatch point: AutomationBinding → Jinja2 expression string.
 * Dispatches purely on `binding.strategy` — never a property name or
 * widget type.
 *
 * @param {import('../ast/AutomationBinding.js').AutomationBinding} binding
 * @returns {string}
 */
export function buildBindingExpression(binding) {
  // A binding without a concrete entity is incomplete. In particular, stale
  // Template-source bindings from the short-lived dynamic-entity experiment
  // must not become states("") or leak {{ tag }} into generated YAML.
  if (['state', 'mapping', 'switch'].includes(binding.strategy)
      && !String(binding.entityId ?? '').trim()) return null;
  return BINDING_GENERATORS[binding.strategy](binding);
}
